import resourcesConfig from "@content/resources/config.json";
import usNational from "@content/resources/us-national.json";
import type {
  QuietViaResource,
  ResourceSelection,
  ResourceSetFile,
  ResourcesConfig,
} from "./types";

const config = resourcesConfig as ResourcesConfig;

const SETS: Record<string, ResourceSetFile> = {
  "us-national.json": usNational as ResourceSetFile,
};

function parseDay(isoDate: string): number {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
  if (!m) return Number.NaN;
  return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

function todayUtcDay(): number {
  const now = new Date();
  return Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
}

export function isResourceReviewCurrent(resource: QuietViaResource): boolean {
  const due = parseDay(resource.verification.reviewDue);
  if (Number.isNaN(due)) return false;
  return due >= todayUtcDay();
}

export function isPresentableResource(resource: QuietViaResource): boolean {
  return (
    resource.enabled === true &&
    resource.verification.status === "verified" &&
    isResourceReviewCurrent(resource)
  );
}

function loadResourcesForSet(resourceSetId: string | null | undefined): QuietViaResource[] {
  const fileName =
    resourceSetId && RESOURCE_SET_FILES[resourceSetId]
      ? RESOURCE_SET_FILES[resourceSetId]
      : config.fallbackResourceFile;
  const set = SETS[fileName] ?? SETS[config.fallbackResourceFile];
  if (!set) return [];
  return set.resources.filter(isPresentableResource);
}

const RESOURCE_SET_FILES: Record<string, string> = {
  "us-national": "us-national.json",
};

/**
 * Select verified static resources for in-memory category list.
 * Categories never go in the URL. National fallback when nothing matches.
 * Optional QR resourceSetId selects which static set to use (falls back to national).
 */
export function selectResourcesForCategories(
  categories: string[] | null | undefined,
  resourceSetId?: string | null,
): ResourceSelection {
  const requested =
    categories && categories.length > 0
      ? [...new Set(categories)]
      : ["national_hotline", "survivor_services", "emergency_services"];

  let presentable = loadResourcesForSet(resourceSetId);
  if (presentable.length === 0) {
    presentable = loadResourcesForSet("us-national");
  }
  const byId = new Map(presentable.map((r) => [r.id, r]));

  const matched: QuietViaResource[] = [];
  const matchedCategories: string[] = [];
  const unavailableCategories: string[] = [];

  for (const category of requested) {
    const hits = presentable.filter((r) =>
      r.categories.includes(category as QuietViaResource["categories"][number]),
    );
    if (hits.length === 0) {
      unavailableCategories.push(category);
      continue;
    }
    matchedCategories.push(category);
    for (const hit of hits) {
      if (!matched.some((r) => r.id === hit.id)) {
        matched.push(hit);
      }
    }
  }

  let usedNationalFallback = false;
  if (matched.length === 0) {
    usedNationalFallback = true;
    for (const id of config.nationalFallbackResourceIds) {
      const resource = byId.get(id);
      if (resource && !matched.some((r) => r.id === resource.id)) {
        matched.push(resource);
      }
    }
    if (matched.length === 0) {
      const anyNational = presentable.find((r) =>
        r.categories.includes("national_hotline"),
      );
      if (anyNational) matched.push(anyNational);
    }
  } else if (unavailableCategories.length > 0) {
    for (const id of config.nationalFallbackResourceIds) {
      const resource = byId.get(id);
      if (resource && !matched.some((r) => r.id === resource.id)) {
        matched.push(resource);
        usedNationalFallback = true;
      }
    }
  }

  return {
    resources: matched,
    matchedCategories,
    unavailableCategories,
    usedNationalFallback,
    pilotJurisdictionPending:
      config.pilotJurisdiction.status === "pending_owner_confirmation",
  };
}

export function getResourcesConfig(): ResourcesConfig {
  return config;
}
